import { styled } from "styled-components";
import { useEffect, useMemo } from "react";
import { OverlayArrow } from "react-aria-components";
import { Text } from "@fidelix/fx-miranda";
import { DateTime } from "luxon";
import {
  useHistoryReplayContext,
  useHistoryReplayPlayheadContext,
} from "./history-replay-context";
import {
  convertTimestampSecondsToTimelinePixels,
  updateGraphicsValues,
} from "./history-replay.utils";
import { useGraphicsContext } from "../graphics-context";

const PLAYHEAD_UPDATE_INTERVAL_MILLIS = 100;

export function HistoryTimeLinePlayhead() {
  const {
    isPlaying,
    historyIsFetching,
    timeLineContainerSize,
    isLoading,
    playbackSpeed,
    timelineDomainInSeconds,
  } = useHistoryReplayContext();
  const { playheadXInSeconds, historyData, translatePlayhead } =
    useHistoryReplayPlayheadContext();

  const { element, pointsQuery } = useGraphicsContext();

  const pointKeys = useMemo(
    () =>
      pointsQuery.data?.points.map((p) => `${p.device.externalId}.${p.name}`) ??
      [],
    [pointsQuery.data],
  );

  const speed = historyIsFetching ? 0 : playbackSpeed;

  // Move the playhead based on real elapsed wall-clock time (via requestAnimationFrame).
  useEffect(() => {
    if (!isPlaying) {
      return;
    }

    let frameId: number;
    let lastTimeMillis = performance.now();

    const tick = (nowMillis: number) => {
      if (nowMillis - lastTimeMillis < PLAYHEAD_UPDATE_INTERVAL_MILLIS) {
        frameId = requestAnimationFrame(tick);
        return;
      }

      const elapsedSeconds = (nowMillis - lastTimeMillis) / 1000;
      lastTimeMillis = nowMillis;
      translatePlayhead(elapsedSeconds * speed);
      frameId = requestAnimationFrame(tick);
    };

    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [isPlaying, speed, translatePlayhead]);

  // Update graphics values when playhead moves
  useEffect(() => {
    if (historyData && element && !historyIsFetching) {
      const timestampSeconds = DateTime.fromSeconds(playheadXInSeconds);
      element.setOverrideTime(timestampSeconds.toJSDate());

      updateGraphicsValues({
        timestampSeconds,
        isFetching: historyIsFetching,
        isPlaying,
        historyData,
        element,
        pointKeys,
      });
    }
  }, [
    element,
    isPlaying,
    historyData,
    historyIsFetching,
    playheadXInSeconds,
    pointKeys,
  ]);

  const playheadXPixels = convertTimestampSecondsToTimelinePixels(
    playheadXInSeconds,
    timelineDomainInSeconds,
    timeLineContainerSize.width,
  );

  return (
    <PlayHeadContainer style={{ left: playheadXPixels }}>
      <Tooltip>
        <Text variant="bodyLight" color="white">
          {DateTime.fromSeconds(playheadXInSeconds).toLocaleString(
            DateTime.TIME_WITH_SECONDS,
          )}
        </Text>
      </Tooltip>
      <Arrow>
        <svg width={12} height={12} viewBox="0 0 8 8">
          <path d="M0 0 L4 4 L8 0" />
        </svg>
      </Arrow>
      <PlayHead $isDisabled={isLoading} />
    </PlayHeadContainer>
  );
}
const Tooltip = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  height: 33px;
  min-width: 80px;
  left: -41px;
  bottom: 5px;
  background: ${(p) => p.theme.colors.mutedDarkNeutral};
  border-radius: 4px;
  white-space: nowrap;
  padding-left: ${(p) => p.theme.space("xsmall")};
  padding-right: ${(p) => p.theme.space("xsmall")};
  z-index: 1;
`;

const Arrow = styled(OverlayArrow)`
  position: absolute;
  top: -10px;
  svg {
    fill: ${(p) => p.theme.colors.mutedDarkNeutral};
  }
`;

const PlayHeadContainer = styled.div`
  position: relative;
  bottom: 73px;
  width: 1px;
  background: transparent;
  z-index: 2;
  pointer-events: none;
`;

const PlayHead = styled.div<{ $isDisabled: boolean }>`
  position: absolute;
  background: white;
  border-radius: 50px;
  width: 20px;
  height: 20px;
  border: 7px solid
    ${(p) =>
      p.$isDisabled ? p.theme.colors.lightNeutral : p.theme.colors.primary};
  left: -10px;
`;
